import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { Check, Copy, ExternalLink, Pencil, Upload, X } from 'lucide-react';
import { useAuthedApi } from '../../lib/useAuthedApi';
import { uploadFile } from '../../lib/uploader';
import RemindersCard from './RemindersCard';
import Toggle from '../../components/Toggle';
import QrCodeButton from '../../components/QrCodeButton';
import { bookingUrl } from '../../lib/bookingUrl';

type Org = {
  id: string;
  name: string;
  slug: string;
  default_timezone: string;
  brand_name: string | null;
  brand_logo_url: string | null;
  brand_color: string | null;
  confirmation_message: string | null; // legacy — superseded by the split email/SMS fields
  confirmation_message_email: string | null;
  confirmation_message_sms: string | null;
  followup_enabled: number;
  followup_delay_minutes: number;
  followup_subject: string | null;
  followup_body: string | null;
  followup_url: string | null;
  checkin_email_enabled: number;
  checkin_open_before_minutes: number;
  checkin_open_after_minutes: number;
  buffer_before_minutes: number;
  buffer_after_minutes: number;
  webhook_url: string | null;
  webhook_secret: string | null;
};

/**
 * Common timezones grouped by region. We never expose a free-text input —
 * IANA strings like "America/Toronto" are too easy to typo, and a wrong
 * value cascades into every booking calculation.
 */
const TIMEZONES: { region: string; zones: { value: string; label: string }[] }[] = [
  {
    region: 'Canada',
    zones: [
      { value: 'America/Halifax', label: 'Atlantic — Halifax' },
      { value: 'America/Toronto', label: 'Eastern — Toronto' },
      { value: 'America/Winnipeg', label: 'Central — Winnipeg' },
      { value: 'America/Regina', label: 'Saskatchewan — Regina (no DST)' },
      { value: 'America/Edmonton', label: 'Mountain — Edmonton' },
      { value: 'America/Vancouver', label: 'Pacific — Vancouver' },
    ],
  },
  {
    region: 'United States',
    zones: [
      { value: 'America/New_York', label: 'Eastern — New York' },
      { value: 'America/Chicago', label: 'Central — Chicago' },
      { value: 'America/Denver', label: 'Mountain — Denver' },
      { value: 'America/Phoenix', label: 'Arizona — Phoenix (no DST)' },
      { value: 'America/Los_Angeles', label: 'Pacific — Los Angeles' },
      { value: 'America/Anchorage', label: 'Alaska — Anchorage' },
      { value: 'Pacific/Honolulu', label: 'Hawaii — Honolulu' },
    ],
  },
  {
    region: 'Europe',
    zones: [
      { value: 'Europe/London', label: 'London' },
      { value: 'Europe/Dublin', label: 'Dublin' },
      { value: 'Europe/Paris', label: 'Paris' },
      { value: 'Europe/Berlin', label: 'Berlin' },
      { value: 'Europe/Madrid', label: 'Madrid' },
      { value: 'Europe/Rome', label: 'Rome' },
      { value: 'Europe/Amsterdam', label: 'Amsterdam' },
    ],
  },
  {
    region: 'Asia / Pacific',
    zones: [
      { value: 'Asia/Tokyo', label: 'Tokyo' },
      { value: 'Asia/Singapore', label: 'Singapore' },
      { value: 'Asia/Hong_Kong', label: 'Hong Kong' },
      { value: 'Asia/Dubai', label: 'Dubai' },
      { value: 'Asia/Kolkata', label: 'India — Kolkata' },
      { value: 'Australia/Sydney', label: 'Sydney' },
      { value: 'Australia/Melbourne', label: 'Melbourne' },
      { value: 'Pacific/Auckland', label: 'Auckland' },
    ],
  },
];

// --- Post-booking message preview ------------------------------------------
// Sample values used only to render the live preview beneath the editor, so the
// admin sees how their placeholders (especially the meeting-type-aware
// {{meeting_details}}) expand before saving.
type PreviewMeetingType = 'in_person' | 'video_call' | 'phone_call';

// Placeholders an admin can drop into the post-booking message. Mirrors
// ALLOWED_TOKENS in src/lib/notify/confirmation-message.ts — keep in sync.
// Rendered as click-to-insert buttons so admins never hand-type (and misspell)
// a token. `{{meeting_details}}` is a self-contained, meeting-type-aware
// sentence.
const POST_BOOKING_TOKENS: { token: string; label: string }[] = [
  { token: 'bookee_name', label: 'Customer name' },
  { token: 'start_time', label: 'Start time' },
  { token: 'staff_name', label: 'Staff name' },
  { token: 'service_name', label: 'Service' },
  { token: 'location_name', label: 'Location' },
  { token: 'location_address', label: 'Address' },
  { token: 'meeting_details', label: 'Meeting details' },
];

const PREVIEW_SAMPLE = {
  bookee_name: 'Jordan Smith',
  start_time: 'Mon, Jun 30 at 9:30 AM',
  duration_minutes: 45,
  location_name: 'Downtown Office',
  location_address: '123 Main St, Regina',
  staff_name: 'Alex Lee',
  service_name: 'Consultation',
  meeting_link: 'https://meet.example.com/abc-defg-hij',
};

// The "Where" line the confirmation email shows, per meeting type — mirrors
// whereHtml() in src/lib/notify/templates.ts closely enough for a preview.
function confirmationWherePreview(t: PreviewMeetingType): string {
  if (t === 'video_call') return PREVIEW_SAMPLE.meeting_link;
  return `${PREVIEW_SAMPLE.location_name} — ${PREVIEW_SAMPLE.location_address}`;
}

// Suggested length for the SMS note before it likely spills into a second
// segment. Advisory only — the real send never truncates (see renderSms in
// confirmation-message.ts), so the preview shows the full note too.
const SMS_SUGGESTED_EXTRA = 80;

// Mirrors meetingDetailsSentence() in src/lib/notify/confirmation-message.ts.
// Keep the wording in sync with the backend when either changes.
function meetingDetailsPreview(t: PreviewMeetingType): string {
  const when = PREVIEW_SAMPLE.start_time;
  if (t === 'video_call') {
    return `Your video meeting at ${when} using this link: ${PREVIEW_SAMPLE.meeting_link}`;
  }
  if (t === 'phone_call') return `Your phone call at ${when}.`;
  return `Your in-person meeting at ${when} at ${PREVIEW_SAMPLE.location_address}`;
}

function renderMessagePreview(message: string, t: PreviewMeetingType): string {
  const values: Record<string, string> = {
    bookee_name: PREVIEW_SAMPLE.bookee_name,
    start_time: PREVIEW_SAMPLE.start_time,
    location_name: PREVIEW_SAMPLE.location_name,
    location_address: PREVIEW_SAMPLE.location_address,
    staff_name: PREVIEW_SAMPLE.staff_name,
    service_name: PREVIEW_SAMPLE.service_name,
    meeting_details: meetingDetailsPreview(t),
  };
  return message.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (full, name: string) =>
    name in values ? values[name]! : full,
  );
}

export default function OrgSettings() {
  const apiFn = useAuthedApi();
  const [org, setOrg] = useState<Org | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editingSlug, setEditingSlug] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  // Meeting type previewed in the post-booking message preview pane.
  const [previewType, setPreviewType] = useState<PreviewMeetingType>('in_person');
  const logoInputRef = useRef<HTMLInputElement>(null);
  const emailMsgRef = useRef<HTMLTextAreaElement>(null);
  const smsMsgRef = useRef<HTMLTextAreaElement>(null);
  const [form, setForm] = useState({
    slug: '',
    brand_name: '',
    brand_color: '',
    confirmation_message_email: '',
    confirmation_message_sms: '',
    default_timezone: '',
    followup_enabled: false,
    followup_delay_value: 2,
    followup_delay_unit: 'hours' as 'minutes' | 'hours',
    followup_url: '',
    followup_subject: '',
    followup_body: '',
    checkin_email_enabled: false,
    checkin_open_before_minutes: 60,
    checkin_open_after_minutes: 120,
    buffer_before_minutes: 15,
    buffer_after_minutes: 15,
  });

  useEffect(() => {
    apiFn<Org>('/organizations/me')
      .then((o) => {
        setOrg(o);
        const delayMin = o.followup_delay_minutes ?? 120;
        const useHours = delayMin % 60 === 0;
        setForm({
          slug: o.slug,
          brand_name: o.brand_name ?? '',
          brand_color: o.brand_color ?? '',
          confirmation_message_email: o.confirmation_message_email ?? '',
          confirmation_message_sms: o.confirmation_message_sms ?? '',
          default_timezone: o.default_timezone,
          followup_enabled: !!o.followup_enabled,
          followup_delay_value: useHours ? delayMin / 60 : delayMin,
          followup_delay_unit: useHours ? 'hours' : 'minutes',
          followup_url: o.followup_url ?? '',
          followup_subject: o.followup_subject ?? '',
          followup_body: o.followup_body ?? '',
          checkin_email_enabled: !!o.checkin_email_enabled,
          checkin_open_before_minutes: o.checkin_open_before_minutes ?? 60,
          checkin_open_after_minutes: o.checkin_open_after_minutes ?? 120,
          buffer_before_minutes: o.buffer_before_minutes ?? 15,
          buffer_after_minutes: o.buffer_after_minutes ?? 15,
        });
      })
      .catch((e) => setErr(e.message));
  }, [apiFn]);

  async function saveSettings() {
    setSaving(true);
    setErr(null);
    try {
      const updated = await apiFn<Org>('/organizations/me', {
        method: 'PATCH',
        body: { default_timezone: form.default_timezone },
      });
      setOrg(updated);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function saveBranding() {
    setSaving(true);
    setErr(null);
    try {
      const updated = await apiFn<Org>('/organizations/me', {
        method: 'PATCH',
        body: { brand_name: form.brand_name.trim() || null },
      });
      setOrg(updated);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function saveBrandColor(color: string | null) {
    setSaving(true);
    setErr(null);
    try {
      const updated = await apiFn<Org>('/organizations/me', {
        method: 'PATCH',
        body: { brand_color: color },
      });
      setOrg(updated);
      setForm((f) => ({ ...f, brand_color: updated.brand_color ?? '' }));
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function pickLogo(file: File) {
    setUploadingLogo(true);
    setErr(null);
    try {
      const { url } = await uploadFile(file);
      const updated = await apiFn<Org>('/organizations/me', {
        method: 'PATCH',
        body: { brand_logo_url: url },
      });
      setOrg(updated);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  }

  async function removeLogo() {
    setSaving(true);
    setErr(null);
    try {
      const updated = await apiFn<Org>('/organizations/me', {
        method: 'PATCH',
        body: { brand_logo_url: null },
      });
      setOrg(updated);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  // Insert a {{token}} at the caret of the focused message textarea, so admins
  // never hand-type (and typo) a placeholder. Restores focus + caret after the
  // controlled re-render.
  function insertToken(
    ref: RefObject<HTMLTextAreaElement>,
    field: 'confirmation_message_email' | 'confirmation_message_sms',
    token: string,
  ) {
    const snippet = `{{${token}}}`;
    const el = ref.current;
    const current = form[field];
    const start = el?.selectionStart ?? current.length;
    const end = el?.selectionEnd ?? current.length;
    const next = current.slice(0, start) + snippet + current.slice(end);
    const caret = start + snippet.length;
    setForm((f) => ({ ...f, [field]: next }));
    requestAnimationFrame(() => {
      if (ref.current) {
        ref.current.focus();
        ref.current.setSelectionRange(caret, caret);
      }
    });
  }

  async function saveConfirmationMessage() {
    setSaving(true);
    setErr(null);
    try {
      const updated = await apiFn<Org>('/organizations/me', {
        method: 'PATCH',
        body: {
          confirmation_message_email: form.confirmation_message_email.trim() || null,
          confirmation_message_sms: form.confirmation_message_sms.trim() || null,
        },
      });
      setOrg(updated);
    } catch (e) {
      // Server rejects messages with unknown placeholders so admins don't ship
      // {{undefined}} text. Surface the bad token so they can fix it in place.
      const body = (e as { body?: { error?: string; token?: string } }).body;
      if (body?.error === 'unknown_token' && body.token) {
        setErr(`Unknown placeholder: {{${body.token}}}. Check the supported placeholders below.`);
      } else {
        setErr((e as Error).message);
      }
    } finally {
      setSaving(false);
    }
  }

  async function saveFollowup() {
    setSaving(true);
    setErr(null);
    try {
      const delayMinutes =
        form.followup_delay_unit === 'hours'
          ? form.followup_delay_value * 60
          : form.followup_delay_value;
      const updated = await apiFn<Org>('/organizations/me', {
        method: 'PATCH',
        body: {
          followup_enabled: form.followup_enabled,
          followup_delay_minutes: delayMinutes,
          followup_url: form.followup_url.trim() || null,
          followup_subject: form.followup_subject.trim() || null,
          followup_body: form.followup_body.trim() || null,
        },
      });
      setOrg(updated);
    } catch (e) {
      const body = (e as { body?: { error?: string; token?: string } }).body;
      if (body?.error === 'unknown_token' && body.token) {
        setErr(`Unknown placeholder: {{${body.token}}}. Check the supported placeholders below.`);
      } else {
        setErr((e as Error).message);
      }
    } finally {
      setSaving(false);
    }
  }

  async function saveSlug() {
    setSaving(true);
    setErr(null);
    try {
      const updated = await apiFn<Org>('/organizations/me', {
        method: 'PATCH',
        body: { slug: form.slug.trim() },
      });
      setOrg(updated);
      setEditingSlug(false);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function saveCheckin() {
    setSaving(true);
    setErr(null);
    try {
      const updated = await apiFn<Org>('/organizations/me', {
        method: 'PATCH',
        body: {
          checkin_email_enabled: form.checkin_email_enabled,
          checkin_open_before_minutes: form.checkin_open_before_minutes,
          checkin_open_after_minutes: form.checkin_open_after_minutes,
        },
      });
      setOrg(updated);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function saveBuffers() {
    setSaving(true);
    setErr(null);
    try {
      const updated = await apiFn<Org>('/organizations/me', {
        method: 'PATCH',
        body: {
          buffer_before_minutes: form.buffer_before_minutes,
          buffer_after_minutes: form.buffer_after_minutes,
        },
      });
      setOrg(updated);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function copyBookingLink() {
    if (!org) return;
    try {
      await navigator.clipboard.writeText(bookingUrl(org.slug));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard API unavailable (non-HTTPS dev or older browser).
      // Surface a visible affordance instead of silently failing.
      alert(`Copy this link manually:\n${bookingUrl(org.slug)}`);
    }
  }

  if (err && !org) return <p className="err">{err}</p>;
  if (!org) return <p className="muted">Loading…</p>;

  return (
    <div>
      {/* --- Organization basics ------------------------------ */}
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Your organization</h2>

        <div className="field-section">
          <h3>Organization name</h3>
          <div className="readonly-field">{org.name}</div>
          <p className="field-help">
            Pulled from your account. Rename it from your avatar menu, top-right.
          </p>
        </div>

        <div className="field-section">
          <h3>Default timezone</h3>
          <select
            value={form.default_timezone}
            onChange={(e) => setForm({ ...form, default_timezone: e.target.value })}
          >
            {TIMEZONES.map((group) => (
              <optgroup key={group.region} label={group.region}>
                {group.zones.map((tz) => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <p className="field-help">
            Fallback when a location or staff member doesn&rsquo;t set their own.
          </p>
        </div>

        {err && <p className="err">{err}</p>}
        <button className="primary" onClick={saveSettings} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      {/* --- Branding ---------------------------------------- */}
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Branding</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          Shown on your confirmation page and the emails &amp; texts we send, so
          customers recognize who it&rsquo;s from.
        </p>

        <div className="field-section">
          <h3>Display name</h3>
          <input
            value={form.brand_name}
            onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
            placeholder={org.name}
            maxLength={120}
          />
          <p className="field-help">
            Sender name on confirmations. Defaults to <strong>{org.name}</strong> if blank.
          </p>
          <button
            className="primary"
            onClick={saveBranding}
            disabled={saving || (form.brand_name.trim() || null) === org.brand_name}
            style={{ marginTop: '0.5rem' }}
          >
            {saving ? 'Saving…' : 'Save display name'}
          </button>
        </div>

        <div className="field-section">
          <h3>Logo</h3>
          {org.brand_logo_url ? (
            <div className="row" style={{ gap: '0.75rem', alignItems: 'center' }}>
              <img
                src={org.brand_logo_url}
                alt={org.brand_name ?? org.name}
                style={{
                  maxWidth: 140,
                  maxHeight: 72,
                  objectFit: 'contain',
                  background: '#fff',
                  border: '1px solid #eaeaea',
                  borderRadius: 4,
                  padding: 4,
                }}
              />
              <button
                type="button"
                className="ghost"
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadingLogo}
              >
                <Upload size={14} /> {uploadingLogo ? 'Uploading…' : 'Replace'}
              </button>
              <button
                type="button"
                className="danger"
                onClick={removeLogo}
                disabled={saving || uploadingLogo}
              >
                <X size={14} /> Remove
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="primary"
              onClick={() => logoInputRef.current?.click()}
              disabled={uploadingLogo}
            >
              <Upload size={14} /> {uploadingLogo ? 'Uploading…' : 'Upload logo'}
            </button>
          )}
          <input
            ref={logoInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void pickLogo(f);
            }}
          />
          <p className="field-help">
            PNG or SVG, transparent background. Wide (~300&times;80) or square
            (~1:1) both work &mdash; we size each to fit the header.
          </p>
        </div>

        <div className="field-section">
          <h3>Brand color</h3>
          <p className="field-help" style={{ marginTop: 0 }}>
            Used for the buttons on your public booking pages. Leave unset to use
            the Invitey default.
          </p>
          <div className="row" style={{ gap: '0.6rem', alignItems: 'center' }}>
            <input
              type="color"
              className="color-swatch"
              value={form.brand_color || '#EF5B3B'}
              onChange={(e) => setForm({ ...form, brand_color: e.target.value })}
              aria-label="Brand color"
            />
            <code>{form.brand_color || 'default'}</code>
            <button
              className="primary"
              onClick={() => saveBrandColor(form.brand_color || null)}
              disabled={saving || (form.brand_color || null) === (org.brand_color || null)}
            >
              {saving ? 'Saving…' : 'Save color'}
            </button>
            {org.brand_color && (
              <button className="ghost" onClick={() => saveBrandColor(null)} disabled={saving}>
                Reset to default
              </button>
            )}
          </div>
        </div>
      </div>

      {/* --- Booking link ------------------------------------ */}
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Your booking link</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          Share this anywhere &mdash; website, bio, emails. Customers pick a
          service, staff member, and time themselves.
        </p>

        <div className="url-display">
          <code>{bookingUrl(org.slug)}</code>
          <button type="button" onClick={copyBookingLink}>
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied' : 'Copy'}
          </button>
          <QrCodeButton
            url={bookingUrl(org.slug)}
            title={org.name}
            filename={org.slug}
          />
          <a
            href={bookingUrl(org.slug)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn"
          >
            <ExternalLink size={14} /> Open
          </a>
        </div>

        {!editingSlug && (
          <div className="row" style={{ marginTop: '0.85rem' }}>
            <button type="button" className="ghost" onClick={() => setEditingSlug(true)}>
              <Pencil size={14} /> Customize URL
            </button>
          </div>
        )}

        {editingSlug && (
          <div style={{ marginTop: '1rem' }}>
            <p className="warn-text">
              Changing this breaks links you&rsquo;ve already shared. Only do it
              if you haven&rsquo;t sent the current one out.
            </p>
            <div className="slug-input-row">
              <span className="prefix">invitey.ca/</span>
              <input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="your-business"
              />
            </div>
            <p className="field-help">
              Letters, numbers, and dashes only. No spaces. Keep it short.
            </p>
            <div className="row" style={{ gap: '0.5rem', marginTop: '0.75rem' }}>
              <button
                className="primary"
                onClick={saveSlug}
                disabled={saving || form.slug.trim() === org.slug || !form.slug.trim()}
              >
                {saving ? 'Saving…' : 'Update URL'}
              </button>
              <button
                className="ghost"
                onClick={() => {
                  setEditingSlug(false);
                  setForm({ ...form, slug: org.slug });
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* --- Booking buffers --------------------------------- */}
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Booking buffers</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          Padding kept free before and after every appointment &mdash; travel,
          notes, cleanup, back-to-back breathing room. Applies to all services.
        </p>
        <div className="row" style={{ gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="number"
            min={0}
            max={240}
            value={form.buffer_before_minutes}
            onChange={(e) =>
              setForm({
                ...form,
                buffer_before_minutes: Math.min(240, Math.max(0, parseInt(e.target.value, 10) || 0)),
              })
            }
            style={{ width: 90 }}
          />
          <span className="muted">min before, and</span>
          <input
            type="number"
            min={0}
            max={240}
            value={form.buffer_after_minutes}
            onChange={(e) =>
              setForm({
                ...form,
                buffer_after_minutes: Math.min(240, Math.max(0, parseInt(e.target.value, 10) || 0)),
              })
            }
            style={{ width: 90 }}
          />
          <span className="muted">min after each appointment</span>
        </div>
        <p className="field-help">Set either to 0 for no buffer on that side.</p>
        {err && <p className="err">{err}</p>}
        <button
          className="primary"
          onClick={saveBuffers}
          disabled={
            saving ||
            (form.buffer_before_minutes === (org.buffer_before_minutes ?? 15) &&
              form.buffer_after_minutes === (org.buffer_after_minutes ?? 15))
          }
          style={{ marginTop: '0.5rem' }}
        >
          {saving ? 'Saving…' : 'Save buffers'}
        </button>
      </div>

      {/* --- Reminders --------------------------------------- */}
      <RemindersCard brandName={form.brand_name.trim() || org.name} brandLogoUrl={org.brand_logo_url} />

      {/* --- Post-booking message (collapsed) ---------------- */}
      <details className="dev-settings">
        <summary>Add to the booking confirmation</summary>
        <div className="dev-settings-body">
          <p className="muted" style={{ marginTop: 0 }}>
            Every booking already gets a confirmation with the appointment
            details, your logo, and signature. Add your own note below and
            we&rsquo;ll append it &mdash; parking, what to bring, &ldquo;arrive
            10 min early.&rdquo; Write the email and text versions separately.
          </p>

          {/* Meeting-type switch drives {{meeting_details}} in BOTH previews. */}
          <div className="msg-preview-head" style={{ marginBottom: '0.75rem' }}>
            <span className="muted small">Preview as</span>
            <div className="seg">
              {([
                ['in_person', 'In person'],
                ['video_call', 'Video'],
                ['phone_call', 'Phone'],
              ] as [PreviewMeetingType, string][]).map(([val, label]) => (
                <button
                  key={val}
                  className={previewType === val ? 'seg-on' : ''}
                  onClick={() => setPreviewType(val)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Email message */}
          <div className="field-section">
            <h3>Email note</h3>
            <div className="token-btns">
              <span className="muted small">Insert:</span>
              {POST_BOOKING_TOKENS.map((t) => (
                <button
                  key={t.token}
                  type="button"
                  className="token-btn"
                  onClick={() => insertToken(emailMsgRef, 'confirmation_message_email', t.token)}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <textarea
              ref={emailMsgRef}
              rows={5}
              value={form.confirmation_message_email}
              onChange={(e) =>
                setForm({ ...form, confirmation_message_email: e.target.value })
              }
              placeholder="Please arrive 10 minutes early and bring a piece of ID. Free parking is out front."
              maxLength={2000}
              style={{ fontFamily: 'inherit' }}
            />
            <p className="field-help">
              Appended below the appointment details, on the confirmation page and
              email. Pasted URLs auto-link; your signature and footer are added
              automatically. Leave blank to add nothing.
            </p>
            {/* Full confirmation-email preview: the automatic details plus the
                admin's note in place, so they see the whole email — mirrors
                bookingConfirmed() in src/lib/notify/templates.ts. */}
            <div className="email-preview">
              <div className="email-preview-brand">
                {org.brand_logo_url ? (
                  <img src={org.brand_logo_url} alt={form.brand_name.trim() || org.name} />
                ) : (
                  <strong>{form.brand_name.trim() || org.name}</strong>
                )}
              </div>
              <div className="email-preview-body">
                <p style={{ marginTop: 0 }}>Hi {PREVIEW_SAMPLE.bookee_name},</p>
                <p>Your appointment is confirmed.</p>
                <ul className="email-preview-list">
                  <li>
                    <b>What:</b> {PREVIEW_SAMPLE.service_name} with {PREVIEW_SAMPLE.staff_name}
                  </li>
                  <li>
                    <b>When:</b> {PREVIEW_SAMPLE.start_time} ({PREVIEW_SAMPLE.duration_minutes} min)
                  </li>
                  <li>
                    <b>Where:</b> {confirmationWherePreview(previewType)}
                  </li>
                </ul>
                {form.confirmation_message_email.trim() ? (
                  <p className="email-preview-note">
                    {renderMessagePreview(form.confirmation_message_email, previewType)}
                  </p>
                ) : (
                  <p className="email-preview-note-empty">Your note appears here</p>
                )}
                <p className="email-preview-sig" style={{ marginBottom: 0 }}>
                  — {form.brand_name.trim() || org.name}
                </p>
                <p style={{ margin: '16px 0 0' }}>
                  <span className="email-preview-btn">Cancel or reschedule</span>
                </p>
              </div>
              <div className="email-preview-foot">Sent with Invitey · appointment scheduling</div>
            </div>
          </div>

          {/* SMS message */}
          <div className="field-section">
            <h3>Text (SMS) note</h3>
            <div className="token-btns">
              <span className="muted small">Insert:</span>
              {POST_BOOKING_TOKENS.map((t) => (
                <button
                  key={t.token}
                  type="button"
                  className="token-btn"
                  onClick={() => insertToken(smsMsgRef, 'confirmation_message_sms', t.token)}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <textarea
              ref={smsMsgRef}
              rows={2}
              value={form.confirmation_message_sms}
              onChange={(e) =>
                setForm({ ...form, confirmation_message_sms: e.target.value })
              }
              placeholder="Arrive 10 min early — free parking out front."
              maxLength={320}
              style={{ fontFamily: 'inherit' }}
            />
            <p className="field-help">
              Appended to the confirmation text. Sent in full &mdash; but keep it
              short: past ~{SMS_SUGGESTED_EXTRA} characters the message likely
              spans multiple segments, which costs more per send. Leave blank to
              send no extra text.
            </p>
            <div className="msg-preview">
              <div className="msg-preview-head">
                <span className="muted small">Full text preview</span>
              </div>
              <div className="msg-preview-body">
                {(() => {
                  const brand = form.brand_name.trim() || org.name;
                  const note = form.confirmation_message_sms.trim()
                    ? ` ${renderMessagePreview(form.confirmation_message_sms, previewType)}`
                    : '';
                  return `${brand}: ${PREVIEW_SAMPLE.service_name} with ${PREVIEW_SAMPLE.staff_name} confirmed for ${PREVIEW_SAMPLE.start_time}.${note} Reply STOP to opt out.`;
                })()}
              </div>
              {renderMessagePreview(form.confirmation_message_sms, previewType).trim()
                .length > SMS_SUGGESTED_EXTRA && (
                <p className="muted small" style={{ margin: '0.4rem 0 0' }}>
                  Your note is over ~{SMS_SUGGESTED_EXTRA} characters &mdash; still
                  sent in full, but likely as multiple segments.
                </p>
              )}
            </div>
          </div>

          {err && <p className="err">{err}</p>}
          <button
            className="primary"
            onClick={saveConfirmationMessage}
            disabled={
              saving ||
              ((form.confirmation_message_email.trim() || null) ===
                (org.confirmation_message_email || null) &&
                (form.confirmation_message_sms.trim() || null) ===
                  (org.confirmation_message_sms || null))
            }
            style={{ marginTop: '0.5rem' }}
          >
            {saving ? 'Saving…' : 'Save messages'}
          </button>
        </div>
      </details>

      {/* --- Post-meeting follow-up (collapsed) -------------- */}
      <details className="dev-settings">
        <summary>Post-meeting follow-up email</summary>
        <div className="dev-settings-body">
          <p className="muted" style={{ marginTop: 0 }}>
            A one-time email after each appointment &mdash; usually
            &ldquo;thanks, leave a review.&rdquo; Off by default. Includes
            one-click unsubscribe; never sent for cancellations.
          </p>

          <Toggle
            checked={form.followup_enabled}
            onChange={(next) => setForm({ ...form, followup_enabled: next })}
            label="Send a follow-up email after appointments"
          />

          {form.followup_enabled && (
          <>
          <div className="field-section" style={{ marginTop: '0.75rem' }}>
            <h3>When to send</h3>
            <div className="row" style={{ gap: '0.5rem', alignItems: 'center' }}>
              <input
                type="number"
                min={0}
                value={form.followup_delay_value}
                onChange={(e) =>
                  setForm({
                    ...form,
                    followup_delay_value: Math.max(0, parseInt(e.target.value, 10) || 0),
                  })
                }
                style={{ width: 90 }}
              />
              <select
                value={form.followup_delay_unit}
                onChange={(e) =>
                  setForm({ ...form, followup_delay_unit: e.target.value as 'minutes' | 'hours' })
                }
              >
                <option value="minutes">minutes</option>
                <option value="hours">hours</option>
              </select>
              <span className="muted">after the appointment ends</span>
            </div>
          </div>

          <div className="field-section">
            <h3>Review / feedback link</h3>
            <input
              type="url"
              value={form.followup_url}
              placeholder="https://g.page/r/your-google-review-link"
              onChange={(e) => setForm({ ...form, followup_url: e.target.value })}
            />
            <p className="field-help">
              Becomes a &ldquo;Leave a review&rdquo; button &mdash; Google, Yelp,
              or any form URL. Blank sends a plain thank-you.
            </p>
          </div>

          <div className="field-section">
            <h3>Email subject</h3>
            <input
              value={form.followup_subject}
              onChange={(e) => setForm({ ...form, followup_subject: e.target.value })}
              placeholder="How was your visit?"
              maxLength={200}
            />
          </div>

          <div className="field-section">
            <h3>Email message</h3>
            <textarea
              rows={4}
              value={form.followup_body}
              onChange={(e) => setForm({ ...form, followup_body: e.target.value })}
              placeholder="Hi {{bookee_name}}, thanks for visiting! We'd love a quick review."
              maxLength={2000}
              style={{ fontFamily: 'inherit' }}
            />
            <p className="field-help">
              Supports placeholders: <code>{`{{bookee_name}}`}</code>,{' '}
              <code>{`{{start_time}}`}</code>, <code>{`{{staff_name}}`}</code>,{' '}
              <code>{`{{service_name}}`}</code>, <code>{`{{location_name}}`}</code>,{' '}
              <code>{`{{location_address}}`}</code>. Leave blank to use the
              default wording.
            </p>
            {/* Full follow-up email preview — mirrors bookingFollowup() in
                src/lib/notify/templates.ts, including the default subject/body
                shown when the fields are left blank and the "Leave a review"
                button that only appears when a review link is set. */}
            {(() => {
              const brand = form.brand_name.trim() || org.name;
              const subjectSet = !!form.followup_subject.trim();
              const subject = subjectSet
                ? renderMessagePreview(form.followup_subject, previewType)
                : `How was your ${PREVIEW_SAMPLE.service_name}?`;
              const bodySet = !!form.followup_body.trim();
              const body = bodySet
                ? renderMessagePreview(form.followup_body, previewType)
                : `Thanks for visiting ${brand}! We'd love to hear how your ${PREVIEW_SAMPLE.service_name} with ${PREVIEW_SAMPLE.staff_name} went.`;
              const hasReview = !!form.followup_url.trim();
              return (
                <div className="email-preview">
                  <div className="email-preview-subj">
                    <span className="muted small">Subject</span> {subject}
                    {!subjectSet && (
                      <span className="muted small preview-default-tag"> · default</span>
                    )}
                  </div>
                  <div className="email-preview-brand">
                    {org.brand_logo_url ? (
                      <img src={org.brand_logo_url} alt={brand} />
                    ) : (
                      <strong>{brand}</strong>
                    )}
                  </div>
                  <div className="email-preview-body">
                    {!bodySet && (
                      <div className="muted small preview-default-tag">Default wording</div>
                    )}
                    <p style={{ marginTop: 0 }}>Hi {PREVIEW_SAMPLE.bookee_name},</p>
                    <p>{body}</p>
                    {hasReview ? (
                      <p style={{ margin: '18px 0' }}>
                        <span className="email-preview-btn">Leave a review</span>
                      </p>
                    ) : (
                      <p className="muted small" style={{ margin: '18px 0' }}>
                        No review link set here — this location&rsquo;s own review
                        link is used if it has one; otherwise no button appears.
                      </p>
                    )}
                    <p className="email-preview-sig" style={{ marginBottom: 0 }}>
                      — {brand}
                    </p>
                  </div>
                  <div className="email-preview-foot">
                    You&rsquo;re receiving this because you booked with {brand}.
                    Unsubscribe from follow-ups · Sent with Invitey
                  </div>
                </div>
              );
            })()}
          </div>

          {err && <p className="err">{err}</p>}
          <button className="primary" onClick={saveFollowup} disabled={saving}>
            {saving ? 'Saving…' : 'Save follow-up settings'}
          </button>
          </>
          )}
        </div>
      </details>

      {/* --- Self-serve check-in (collapsed) ----------------- */}
      <details className="dev-settings">
        <summary>Self-serve check-in</summary>
        <div className="dev-settings-body">
          <p className="muted" style={{ marginTop: 0 }}>
            Send a dedicated &ldquo;check in&rdquo; email about 15 minutes before
            each in-person appointment, with a one-tap <strong>Check in</strong>{' '}
            button. Tapping it notifies the assigned staff and marks the booking
            checked in. In-person only, and skipped if they&rsquo;ve already
            checked in.
          </p>

          <Toggle
            checked={form.checkin_email_enabled}
            onChange={(next) => setForm({ ...form, checkin_email_enabled: next })}
            label="Send a check-in email before in-person appointments"
          />

          {form.checkin_email_enabled && (
            <>
            <div className="field-section" style={{ marginTop: '0.75rem' }}>
              <h3>Check-in window</h3>
              <p className="field-help" style={{ marginTop: 0 }}>
                How long the check-in link stays active around the appointment.
                Outside it, check-in won&rsquo;t register.
              </p>
              <div className="row" style={{ gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <span className="muted">Opens</span>
                <input
                  type="number"
                  min={0}
                  max={1440}
                  value={form.checkin_open_before_minutes}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      checkin_open_before_minutes: Math.min(
                        1440,
                        Math.max(0, parseInt(e.target.value, 10) || 0),
                      ),
                    })
                  }
                  style={{ width: 90 }}
                />
                <span className="muted">min before start, closes</span>
                <input
                  type="number"
                  min={0}
                  max={1440}
                  value={form.checkin_open_after_minutes}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      checkin_open_after_minutes: Math.min(
                        1440,
                        Math.max(0, parseInt(e.target.value, 10) || 0),
                      ),
                    })
                  }
                  style={{ width: 90 }}
                />
                <span className="muted">min after end</span>
              </div>
            </div>

            {/* What clients receive — the check-in email is fixed content (not
                editable), so this is read-only. Mirrors bookingCheckin() in
                src/lib/notify/templates.ts. In-person only, so "Where" always
                shows the location. */}
            <div className="field-section">
              <h3>What clients receive</h3>
              <p className="field-help" style={{ marginTop: 0 }}>
                This email isn&rsquo;t editable &mdash; here&rsquo;s exactly what
                goes out, ~15 minutes before an in-person appointment.
              </p>
              <div className="email-preview">
                <div className="email-preview-subj">
                  <span className="muted small">Subject</span> It&rsquo;s almost
                  time &mdash; check in for your {PREVIEW_SAMPLE.service_name}
                </div>
                <div className="email-preview-brand">
                  {org.brand_logo_url ? (
                    <img src={org.brand_logo_url} alt={form.brand_name.trim() || org.name} />
                  ) : (
                    <strong>{form.brand_name.trim() || org.name}</strong>
                  )}
                </div>
                <div className="email-preview-body">
                  <p style={{ marginTop: 0 }}>Hi {PREVIEW_SAMPLE.bookee_name},</p>
                  <p>Your appointment is coming up soon:</p>
                  <ul className="email-preview-list">
                    <li>
                      <b>What:</b> {PREVIEW_SAMPLE.service_name} with {PREVIEW_SAMPLE.staff_name}
                    </li>
                    <li>
                      <b>When:</b> {PREVIEW_SAMPLE.start_time}
                    </li>
                    <li>
                      <b>Where:</b> {PREVIEW_SAMPLE.location_name} — {PREVIEW_SAMPLE.location_address}
                    </li>
                  </ul>
                  <p style={{ margin: '18px 0 6px' }}>
                    <span className="email-preview-btn">Check in now</span>
                  </p>
                  <p className="muted small" style={{ margin: 0 }}>
                    Tap the button when you arrive to let {PREVIEW_SAMPLE.staff_name}{' '}
                    know you&rsquo;re here.
                  </p>
                  <p className="email-preview-sig" style={{ marginBottom: 0 }}>
                    — {form.brand_name.trim() || org.name}
                  </p>
                </div>
                <div className="email-preview-foot">Sent with Invitey · appointment scheduling</div>
              </div>
            </div>
            </>
          )}

          {err && <p className="err">{err}</p>}
          <button
            className="primary"
            onClick={saveCheckin}
            disabled={saving}
            style={{ marginTop: '0.75rem' }}
          >
            {saving ? 'Saving…' : 'Save check-in settings'}
          </button>
        </div>
      </details>

      {/* Developer settings (webhooks) hidden for now — no current use case.
          Backend webhook fields remain; re-add the section when needed. */}
    </div>
  );
}
